import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form } from 'react-bootstrap';
import './PartnerCompanyAutocomplete.css';

const DROPDOWN_MAX_HEIGHT = 220;

const PartnerCompanyAutocomplete = ({
    value = '',
    onChange,
    onInputChange,
    options = [],
    loading = false,
    error = null,
    disabled = false,
    placeholder = 'Rechercher un partenaire…',
    hint = null,
    onKeyDown,
    emptyLabel = 'Aucun partenaire trouvé',
    loadingLabel = 'Chargement des partenaires…',
}) => {
    const anchorRef = useRef(null);
    const [search, setSearch] = useState(value || '');
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState(null);

    useEffect(() => {
        setSearch(value || '');
    }, [value]);

    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((name) => name.toLowerCase().includes(query));
    }, [options, search]);

    const updateDropdownPosition = useCallback(() => {
        if (!anchorRef.current) return;

        const rect = anchorRef.current.getBoundingClientRect();
        const maxHeight = Math.max(
            120,
            Math.min(DROPDOWN_MAX_HEIGHT, window.innerHeight - rect.bottom - 12)
        );

        setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            maxHeight,
        });
    }, []);

    useEffect(() => {
        if (!showDropdown) {
            setDropdownPosition(null);
            return undefined;
        }

        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [showDropdown, updateDropdownPosition, filteredOptions.length, loading]);

    const commitValue = (companyName) => {
        const next = companyName || '';
        setSearch(next);
        onChange(next);
        onInputChange?.(next);
    };

    const handleSearchChange = (newValue) => {
        setSearch(newValue);
        onInputChange?.(newValue);
        // Ne pas vider la valeur parent à chaque frappe (sinon le useEffect remet search à '').
        // On conserve le texte saisi ; une sélection exacte reste possible via la liste.
        const exactMatch = options.find(
            (name) => name.toLowerCase() === newValue.trim().toLowerCase()
        );
        onChange(exactMatch || newValue);
        setShowDropdown(true);
    };

    const handleSelect = (companyName) => {
        commitValue(companyName);
        setShowDropdown(false);
    };

    const dropdownMenu = showDropdown && dropdownPosition ? (
        <div
            className="partner-autocomplete-dropdown partner-autocomplete-dropdown--portal"
            style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: dropdownPosition.maxHeight,
            }}
            onMouseDown={(e) => e.preventDefault()}
            role="listbox"
        >
            {loading ? (
                <div className="partner-autocomplete-empty text-muted">{loadingLabel}</div>
            ) : filteredOptions.length === 0 ? (
                <div className="partner-autocomplete-empty text-muted">{emptyLabel}</div>
            ) : (
                filteredOptions.map((companyName) => (
                    <button
                        key={companyName}
                        type="button"
                        className={`partner-autocomplete-option${value === companyName ? ' active' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect(companyName)}
                        role="option"
                        aria-selected={value === companyName}
                    >
                        {companyName}
                    </button>
                ))
            )}
        </div>
    ) : null;

    return (
        <div className={`partner-company-autocomplete${showDropdown ? ' partner-company-autocomplete--open' : ''}`}>
            <div className="partner-autocomplete" ref={anchorRef}>
                <Form.Control
                    type="text"
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => {
                        window.setTimeout(() => setShowDropdown(false), 150);
                    }}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                    aria-expanded={showDropdown}
                    aria-autocomplete="list"
                />
                {search && (
                    <Button
                        type="button"
                        variant="link"
                        className="partner-autocomplete-clear"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect('')}
                        aria-label="Effacer"
                        disabled={disabled || loading}
                    >
                        ×
                    </Button>
                )}
                {dropdownMenu && createPortal(dropdownMenu, document.body)}
            </div>
            {error && (
                <Form.Text className="text-danger d-block mt-1">{error}</Form.Text>
            )}
            {hint && (
                <Form.Text className="text-muted">{hint}</Form.Text>
            )}
        </div>
    );
};

export default PartnerCompanyAutocomplete;
